import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { motion } from 'framer-motion';
import { Users, CalendarDays, Activity, MessageSquare, Plus, Trash2, Send, Phone, MessageCircle, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [tab, setTab] = useState('overview');
  const [eventForm, setEventForm] = useState({ title: '', description: '', date: '', location: '', max_participants: 15, event_type: 'group_karma' });
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({ title: '', description: '', category: 'general', points: 10 });
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  // WhatsApp broadcast state
  const [waMessage, setWaMessage] = useState('');
  const [waSendAll, setWaSendAll] = useState(true);
  const [waSelected, setWaSelected] = useState([]);
  const [waSending, setWaSending] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [s, u, e, a, c, b] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/events`, { withCredentials: true }),
        axios.get(`${API}/activities`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/whatsapp/broadcasts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setUsers(u.data); setEvents(e.data); setActivities(a.data); setContacts(c.data); setBroadcasts(b.data);
    } catch (err) {
      console.error('Admin fetch error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Session expired. Please login again.');
      }
    }
  };

  const createEvent = async () => {
    try {
      await axios.post(`${API}/events`, eventForm, { withCredentials: true });
      toast.success('Event created!');
      setEventDialogOpen(false);
      setEventForm({ title: '', description: '', date: '', location: '', max_participants: 15, event_type: 'group_karma' });
      fetchAll();
    } catch { toast.error('Failed to create event'); }
  };

  const deleteEvent = async (id) => {
    try { await axios.delete(`${API}/events/${id}`, { withCredentials: true }); toast.success('Event deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const createActivity = async () => {
    try {
      await axios.post(`${API}/activities`, activityForm, { withCredentials: true });
      toast.success('Activity created!');
      setActivityDialogOpen(false);
      setActivityForm({ title: '', description: '', category: 'general', points: 10 });
      fetchAll();
    } catch { toast.error('Failed to create activity'); }
  };

  const deleteActivity = async (id) => {
    try { await axios.delete(`${API}/activities/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const sendBroadcast = async () => {
    if (!waMessage.trim()) { toast.error('Message cannot be empty'); return; }
    setWaSending(true);
    try {
      const payload = { message: waMessage, send_to_all: waSendAll, recipient_ids: waSendAll ? [] : waSelected };
      const { data } = await axios.post(`${API}/whatsapp/broadcast`, payload, { withCredentials: true });
      toast.success(`Broadcast sent to ${data.recipient_count} members (mocked)`);
      setWaMessage('');
      setWaSelected([]);
      fetchAll();
    } catch { toast.error('Failed to send broadcast'); }
    finally { setWaSending(false); }
  };

  const toggleUserSelect = (uid) => {
    setWaSelected(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const usersWithPhone = users.filter(u => u.phone);
  const activeMembers = users.filter(u => u.subscription?.status === 'active');

  const statCards = [
    { label: 'Total Members', value: stats?.total_users || 0, icon: Users, color: '#D4A54A' },
    { label: 'Active Subs', value: stats?.active_subscriptions || 0, icon: Activity, color: '#7D9B6A' },
    { label: 'Total Events', value: stats?.total_events || 0, icon: CalendarDays, color: '#C49540' },
    { label: 'New Contacts', value: stats?.pending_contacts || 0, icon: MessageSquare, color: '#8B6914' },
  ];

  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="admin-page">
        <div className="mb-8">
          <p className="font-sans text-xs uppercase tracking-[0.2em] font-bold text-[#D4A54A] mb-1">Admin</p>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Admin Dashboard</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-5">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={1.5} />
                <span className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070]">{s.label}</span>
              </div>
              <p className="font-serif text-3xl font-semibold text-[#F5E6C8]">{s.value}</p>
            </motion.div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#1A1008] border border-[#3D2D1D]/50 rounded-xl mb-6 p-1 flex-wrap">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-lg font-sans text-sm text-[#A89070] data-[state=active]:bg-[#D4A54A] data-[state=active]:text-[#110A06]">Overview</TabsTrigger>
            <TabsTrigger value="members" data-testid="admin-tab-members" className="rounded-lg font-sans text-sm text-[#A89070] data-[state=active]:bg-[#D4A54A] data-[state=active]:text-[#110A06]">Members</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events" className="rounded-lg font-sans text-sm text-[#A89070] data-[state=active]:bg-[#D4A54A] data-[state=active]:text-[#110A06]">Events</TabsTrigger>
            <TabsTrigger value="activities" data-testid="admin-tab-activities" className="rounded-lg font-sans text-sm text-[#A89070] data-[state=active]:bg-[#D4A54A] data-[state=active]:text-[#110A06]">Activities</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="admin-tab-whatsapp" className="rounded-lg font-sans text-sm text-[#A89070] data-[state=active]:bg-[#25D366] data-[state=active]:text-white">WhatsApp</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-lg font-sans text-sm text-[#A89070] data-[state=active]:bg-[#D4A54A] data-[state=active]:text-[#110A06]">Contacts</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
                <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4">Recent Members</h2>
                <div className="space-y-3">
                  {users.slice(0, 5).map(u => (
                    <div key={u.user_id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#23180E] transition-colors">
                      <div className="w-9 h-9 rounded-full bg-[#D4A54A]/10 flex items-center justify-center">
                        <span className="font-sans text-sm font-semibold text-[#D4A54A]">{u.name?.[0]?.toUpperCase() || '?'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm font-medium text-[#F5E6C8]">{u.name}</p>
                        <p className="font-sans text-xs text-[#A89070]">{u.email}</p>
                      </div>
                      <div className="text-right">
                        {u.phone && <p className="font-sans text-xs text-[#A89070] flex items-center gap-1"><Phone className="w-3 h-3" /> {u.phone}</p>}
                        <span className={`px-2 py-0.5 rounded-full font-sans text-xs font-bold uppercase ${u.role === 'admin' ? 'bg-[#D4A54A]/10 text-[#D4A54A]' : 'bg-[#7D9B6A]/10 text-[#7D9B6A]'}`}>
                          {u.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
                <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <button onClick={() => { setTab('whatsapp'); }} data-testid="quick-whatsapp-btn" className="w-full flex items-center gap-3 p-4 rounded-xl border border-[#3D2D1D]/50 hover:border-[#25D366]/50 hover:bg-[#25D366]/5 transition-all">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                    <div className="text-left flex-1">
                      <p className="font-sans text-sm font-medium text-[#F5E6C8]">Send WhatsApp Broadcast</p>
                      <p className="font-sans text-xs text-[#A89070]">{usersWithPhone.length} members with phone</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#A89070]" />
                  </button>
                  <button onClick={() => { setTab('events'); setEventDialogOpen(true); }} className="w-full flex items-center gap-3 p-4 rounded-xl border border-[#3D2D1D]/50 hover:border-[#D4A54A]/30 hover:bg-[#D4A54A]/5 transition-all">
                    <CalendarDays className="w-5 h-5 text-[#D4A54A]" />
                    <div className="text-left flex-1">
                      <p className="font-sans text-sm font-medium text-[#F5E6C8]">Create New Event</p>
                      <p className="font-sans text-xs text-[#A89070]">{events.length} events total</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#A89070]" />
                  </button>
                  <button onClick={() => { setTab('activities'); setActivityDialogOpen(true); }} className="w-full flex items-center gap-3 p-4 rounded-xl border border-[#3D2D1D]/50 hover:border-[#7D9B6A]/30 hover:bg-[#7D9B6A]/5 transition-all">
                    <Activity className="w-5 h-5 text-[#7D9B6A]" />
                    <div className="text-left flex-1">
                      <p className="font-sans text-sm font-medium text-[#F5E6C8]">Add Karma Activity</p>
                      <p className="font-sans text-xs text-[#A89070]">{activities.length} activities</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#A89070]" />
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Members */}
          <TabsContent value="members">
            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 overflow-hidden">
              <div className="p-4 border-b border-[#3D2D1D]/50 flex items-center justify-between">
                <h2 className="font-serif text-lg font-semibold text-[#F5E6C8]">All Members ({users.length})</h2>
                <div className="font-sans text-xs text-[#A89070]">{activeMembers.length} active subscribers</div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#3D2D1D]/50">
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Name</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Email</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Phone</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Role</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Karma</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Subscription</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.user_id} className="border-[#3D2D1D]/30 hover:bg-[#23180E]">
                      <TableCell className="font-sans text-sm text-[#F5E6C8]">{u.name}</TableCell>
                      <TableCell className="font-sans text-sm text-[#A89070]">{u.email}</TableCell>
                      <TableCell className="font-sans text-sm text-[#A89070]">{u.phone || <span className="text-[#5C4830]">-</span>}</TableCell>
                      <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-[#D4A54A]/10 text-[#D4A54A]' : 'bg-[#7D9B6A]/10 text-[#7D9B6A]'}`}>{u.role}</span></TableCell>
                      <TableCell className="font-sans text-sm font-medium text-[#D4A54A]">{u.karma_points || 0}</TableCell>
                      <TableCell className="font-sans text-sm text-[#A89070]">{u.subscription?.tier?.replace('_', ' ') || 'None'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Events */}
          <TabsContent value="events">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8]">Karma Events ({events.length})</h2>
              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogTrigger asChild>
                  <button data-testid="create-event-btn" className="bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-5 py-2 font-sans text-sm font-bold flex items-center gap-2 transition-all duration-200">
                    <Plus className="w-4 h-4" /> New Event
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A1008] border border-[#3D2D1D] rounded-2xl">
                  <DialogHeader><DialogTitle className="font-serif text-xl text-[#F5E6C8]">Create Event</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-3">
                    <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} placeholder="Event title" data-testid="event-title-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 placeholder:text-[#5C4830]" />
                    <Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Description" data-testid="event-desc-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] placeholder:text-[#5C4830]" />
                    <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} data-testid="event-date-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11" />
                    <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="Location" data-testid="event-location-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 placeholder:text-[#5C4830]" />
                    <Input type="number" value={eventForm.max_participants} onChange={(e) => setEventForm({ ...eventForm, max_participants: parseInt(e.target.value) || 15 })} placeholder="Max participants" data-testid="event-max-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 placeholder:text-[#5C4830]" />
                    <button onClick={createEvent} data-testid="confirm-create-event" className="w-full bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full py-3 font-sans font-bold transition-all duration-200">Create Event</button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-3">
              {events.map(evt => (
                <div key={evt.event_id} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-5 flex items-center justify-between hover:border-[#D4A54A]/30 transition-all">
                  <div>
                    <h3 className="font-sans text-sm font-medium text-[#F5E6C8]">{evt.title}</h3>
                    <p className="font-sans text-xs text-[#A89070]">{evt.date} &middot; {evt.location} &middot; {evt.participants?.length || 0}/{evt.max_participants} participants</p>
                  </div>
                  <button onClick={() => deleteEvent(evt.event_id)} data-testid={`delete-event-${evt.event_id}`} className="p-2 text-[#C0392B] hover:bg-[#C0392B]/10 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Activities */}
          <TabsContent value="activities">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8]">Karma Activities ({activities.length})</h2>
              <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
                <DialogTrigger asChild>
                  <button data-testid="create-activity-btn" className="bg-[#7D9B6A] hover:bg-[#6B8A58] text-white rounded-full px-5 py-2 font-sans text-sm font-bold flex items-center gap-2 transition-all duration-200">
                    <Plus className="w-4 h-4" /> New Activity
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1A1008] border border-[#3D2D1D] rounded-2xl">
                  <DialogHeader><DialogTitle className="font-serif text-xl text-[#F5E6C8]">Create Activity</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-3">
                    <Input value={activityForm.title} onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })} placeholder="Activity title" data-testid="activity-title-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 placeholder:text-[#5C4830]" />
                    <Textarea value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} placeholder="Description" data-testid="activity-desc-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] placeholder:text-[#5C4830]" />
                    <Input value={activityForm.category} onChange={(e) => setActivityForm({ ...activityForm, category: e.target.value })} placeholder="Category" data-testid="activity-category-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 placeholder:text-[#5C4830]" />
                    <Input type="number" value={activityForm.points} onChange={(e) => setActivityForm({ ...activityForm, points: parseInt(e.target.value) || 10 })} placeholder="Points" data-testid="activity-points-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11 placeholder:text-[#5C4830]" />
                    <button onClick={createActivity} data-testid="confirm-create-activity" className="w-full bg-[#7D9B6A] hover:bg-[#6B8A58] text-white rounded-full py-3 font-sans font-bold transition-all duration-200">Create Activity</button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-3">
              {activities.map(act => (
                <div key={act.activity_id} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-5 flex items-center justify-between hover:border-[#D4A54A]/30 transition-all">
                  <div>
                    <h3 className="font-sans text-sm font-medium text-[#F5E6C8]">{act.title}</h3>
                    <p className="font-sans text-xs text-[#A89070]">{act.category} &middot; {act.points} pts</p>
                  </div>
                  <button onClick={() => deleteActivity(act.activity_id)} data-testid={`delete-activity-${act.activity_id}`} className="p-2 text-[#C0392B] hover:bg-[#C0392B]/10 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* WhatsApp Broadcast */}
          <TabsContent value="whatsapp">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
                <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" /> Send Broadcast
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Message</label>
                    <Textarea value={waMessage} onChange={(e) => setWaMessage(e.target.value)} data-testid="wa-message-input" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] min-h-[120px] placeholder:text-[#5C4830]" placeholder="Type your WhatsApp broadcast message..." />
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox checked={waSendAll} onCheckedChange={(v) => setWaSendAll(!!v)} data-testid="wa-send-all" className="border-[#3D2D1D] data-[state=checked]:bg-[#25D366] data-[state=checked]:border-[#25D366]" />
                    <label className="font-sans text-sm text-[#F5E6C8]">Send to all members with phone ({usersWithPhone.length})</label>
                  </div>
                  {!waSendAll && (
                    <div className="border border-[#3D2D1D]/50 rounded-xl p-3 max-h-[200px] overflow-y-auto space-y-2">
                      {usersWithPhone.map(u => (
                        <label key={u.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#23180E] cursor-pointer">
                          <Checkbox checked={waSelected.includes(u.user_id)} onCheckedChange={() => toggleUserSelect(u.user_id)} className="border-[#3D2D1D] data-[state=checked]:bg-[#D4A54A] data-[state=checked]:border-[#D4A54A]" />
                          <div className="flex-1">
                            <p className="font-sans text-sm text-[#F5E6C8]">{u.name}</p>
                            <p className="font-sans text-xs text-[#A89070]">{u.phone}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  <button onClick={sendBroadcast} disabled={waSending || !waMessage.trim()} data-testid="send-broadcast-btn" className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full py-3 font-sans font-bold transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" /> {waSending ? 'Sending...' : 'Send WhatsApp Broadcast'}
                  </button>
                  <p className="font-sans text-xs text-[#5C4830] text-center">WhatsApp API integration is mocked. Messages are logged and sent as in-app notifications.</p>
                </div>
              </div>

              <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
                <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4">Broadcast History</h2>
                {broadcasts.length === 0 ? (
                  <p className="font-sans text-sm text-[#A89070] py-6 text-center">No broadcasts sent yet</p>
                ) : (
                  <div className="space-y-3">
                    {broadcasts.map(b => (
                      <div key={b.broadcast_id} className="p-4 rounded-xl border border-[#3D2D1D]/50">
                        <p className="font-sans text-sm text-[#F5E6C8] mb-1">{b.message}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="font-sans text-xs text-[#25D366] bg-[#25D366]/10 px-2 py-0.5 rounded-full">{b.recipient_count} recipients</span>
                          <span className="font-sans text-xs text-[#A89070]">{new Date(b.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          <span className="font-sans text-xs text-[#5C4830] uppercase">{b.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#3D2D1D]/50">
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Name</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Email</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Phone</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Message</TableHead>
                    <TableHead className="font-sans text-xs uppercase tracking-widest text-[#A89070]">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map(c => (
                    <TableRow key={c.contact_id} className="border-[#3D2D1D]/30 hover:bg-[#23180E]">
                      <TableCell className="font-sans text-sm text-[#F5E6C8]">{c.name}</TableCell>
                      <TableCell className="font-sans text-sm text-[#A89070]">{c.email}</TableCell>
                      <TableCell className="font-sans text-sm text-[#A89070]">{c.phone || '-'}</TableCell>
                      <TableCell className="font-sans text-sm text-[#A89070] max-w-[200px] truncate">{c.message}</TableCell>
                      <TableCell className="font-sans text-xs text-[#A89070]">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
